import {
  cancelBookingAdmin,
  getFutureBookingsReport,
} from "../../../Redux/Reports/ReportsActions";
import ConfirmationModal from "../../../Components/Common/ConfirmationModal/ConfirmationModal";
import { ColDef, GridReadyEvent, ICellRendererParams } from "ag-grid-community";
import { getAllBuildings } from "../../../Redux/Buildings/BuildingActions";
import Loading from "../../../Components/Common/Loading/Loading";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import exportToExcel from "../../../Util/exportToExcel";
import { AppDispatch } from "../../../Redux/store";
import React, { useEffect, useState } from "react";
import styles from "./FutureBookings.module.scss";
import { RootState } from "../../../Redux/store";
import { HiDotsVertical } from "react-icons/hi";
import { IoCloseSharp } from "react-icons/io5";
import { FaFilePdf } from "react-icons/fa6";
import { AgGridReact } from "ag-grid-react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import moment from "moment";

interface FutureBookingsType {
  amenityName: string;
  bookingId: number;
  buildingId: number;
  buildingName: string;
  createdAt: string;
  description: string;
  endTime: string;
  memberFullName: string;
  memberId: number;
  pdflink: string;
  startTime: string;
  statusName: string;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

function FutureBookings() {
  const dispatch = useDispatch<AppDispatch>();
  const [search, setSearch] = useState("");
  const [buildingFilter, setBuildingFilter] = useState("");
  const {
    futureBookingsReport,
    futureBookingsReportLoading,
    cancelBookingAdminLoading,
  } = useSelector((state: RootState) => state.reports);

  const { buildings } = useSelector((state: RootState) => state.buildings);
  const [confirmCancelBookingModalOpen, setConfirmCancelBookingModalOpen] =
    useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [rowData, setRowData] = useState<FutureBookingsType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);

  const cancelBookingHandler = () => {
    const params = {
      bookingId: selectedBooking?.bookingId,
      successHandler: () => {
        setConfirmCancelBookingModalOpen(false);
        setSelectedBooking(null);
        dispatch(getFutureBookingsReport());
      },
    };
    if (!cancelBookingAdminLoading) {
      dispatch(cancelBookingAdmin(params));
    }
  };

  const applyFilters = (rowData: FutureBookingsType[]) => {
    let array = [...rowData];

    if (search?.length > 3) {
      array = array.filter(
        (member: FutureBookingsType) =>
          member?.buildingName
            ?.toLowerCase()
            ?.includes(search?.toLowerCase()) ||
          member?.amenityName?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.statusName?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.buildingName
            ?.toLowerCase()
            ?.includes(search?.toLowerCase()) ||
          member?.memberFullName
            ?.toLowerCase()
            ?.includes(search?.toLowerCase()),
      );
    }

    if (buildingFilter) {
      array = array.filter(
        (member: FutureBookingsType) => member?.buildingName === buildingFilter,
      );
    }

    return array;
  };

  const filteredRowData = applyFilters(rowData);

  const columnDefs: ColDef<FutureBookingsType>[] = [
    {
      headerName: "Building Name",
      flex: 1,
      minWidth: 150,
      field: "buildingName",
    },
    {
      headerName: "Amenity Name",
      field: "amenityName",
      flex: 1,
      minWidth: 150,
    },

    {
      headerName: "Member Name",
      field: "memberFullName",
      flex: 1,
      minWidth: 150,
    },

    {
      headerName: "Booking Start Time",
      field: "startTime",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) =>
        moment(params?.data?.startTime).format("MM/DD/YYYY hh:mm A"),
    },

    {
      headerName: "Booking End Time",
      field: "startTime",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) =>
        moment(params?.data?.endTime).format("MM/DD/YYYY hh:mm A"),
    },

    {
      headerName: "Status",
      field: "statusName",
      flex: 1,
      minWidth: 100,
      cellClass: (params) => {
        return params?.data?.statusName === "Completed"
          ? "CompletedText"
          : params?.data?.statusName === "Pending"
            ? "pendingText"
            : "text-black-500";
      },
    },

    {
      headerName: "PDF",
      flex: 1,
      minWidth: 100,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<FutureBookingsType>) => {
        return (
          <div className={styles.pdfContainer}>
            <a
              href={params?.data?.pdflink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaFilePdf className={styles.pdfIcon} size={20} />
            </a>
          </div>
        );
      },
    },

    {
      sortable: false,
      suppressAutoSize: true,
      suppressMovable: true,
      suppressNavigable: true,
      editable: false,
      width: 50,
      cellStyle: {
        pointerEvents: "all",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
      },
      cellRenderer: (params: ICellRendererParams<FutureBookingsType>) => {
        return (
          <div className={styles.iconContainer}>
            <Menu
              direction="left"
              menuButton={
                <MenuButton>
                  <HiDotsVertical className={styles.icon} />
                </MenuButton>
              }
              transition
              portal
            >
              <MenuItem
                disabled={params?.data?.statusName === "Cancelled"}
                onClick={() => {
                  setConfirmCancelBookingModalOpen(true);
                  setSelectedBooking(params?.data);
                }}
              >
                Cancel Booking
              </MenuItem>
            </Menu>
          </div>
        );
      },
    },
  ];

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getFutureBookingsReport());
    dispatch(getAllBuildings());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (futureBookingsReport) {
      setRowData(futureBookingsReport);
    }
  }, [futureBookingsReport]);

  if (futureBookingsReportLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingIcon}>
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className={styles.mainContainer}>
        <div className={styles.topContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Bookings</h1>
          </div>
          <div className={styles.buttonContainer}>
            {filteredRowData?.length > 0 && (
              <button
                className={styles.button}
                onClick={() =>
                  exportToExcel({
                    data: filteredRowData || [],
                    fileName: "Future Bookings Report",
                  })
                }
              >
                Export
              </button>
            )}
          </div>
        </div>
        <div className={styles.bodyContainer}>
          <div className={styles.doubleInputContainer}>
            <div className={styles.searchContainer}>
              <div className={styles.searchContainerInner}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e?.target?.value || "")}
                />
                <div
                  className={styles.iconContainer}
                  onClick={() => setSearch("")}
                >
                  <IoCloseSharp className={styles.icon} />
                </div>
              </div>
            </div>
            <div className={styles.filtersContainer}>
              <div className={styles.filterItem}>
                <p className={styles.label}>Building</p>
                <select
                  className={styles.input}
                  value={buildingFilter}
                  onChange={(e) => setBuildingFilter(e?.target?.value || "")}
                >
                  <option value="">All</option>
                  {buildings?.map((building: any, i: number) => {
                    return (
                      <option key={i} value={building?.buildingName}>
                        {building?.buildingName}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>
          <div className={styles.gridContainer}>
            <AgGridReact
              className={styles.grid}
              rowData={filteredRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20, 50, 100]}
              animateRows={true}
              domLayout="autoHeight"
            />
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmCancelBookingModalOpen}
        setIsOpen={setConfirmCancelBookingModalOpen}
        heading="Cancel Booking"
        text="Are you sure you want to cancel this booking?"
        onConfirm={cancelBookingHandler}
        loading={cancelBookingAdminLoading}
      />
    </React.Fragment>
  );
}

export default FutureBookings;
